import '@banno/jha-wc/src/buttons/jha-button/jha-button';
import '@banno/jha-wc/src/icons/jha-icon-lock';
import { InjectProperty } from '@jack-henry/frontend-utils/di';
import { DynamicComponentExitReason } from '@jack-henry/frontend-utils/lit/dynamic-component-registry';
import { CompanyViewModel } from '@treasury/domain-components/view-models/company.viewModel';
import { CompanyUserService } from '@treasury/domain/company/user';
import { TmBaseComponent } from '@treasury/presentation';
import { TmDialogButtonPosture, TmDialogService } from '@treasury/presentation/services/dialog';
import { TmToastPosture, TmToastService } from '@treasury/presentation/services/toast';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { CompanyUserChallengeDetailsViewModel } from '../../company-user-challenge-details.vm';

@customElement('tm-bo-lock-details')
export class LockDetails extends TmBaseComponent {
    @InjectProperty()
    declare public readonly companyUserService: CompanyUserService;

    @InjectProperty()
    declare public readonly dialogService: TmDialogService;

    @InjectProperty()
    declare public readonly toastService: TmToastService;

    @property({ type: Number })
    public userId?: number;

    @property({ type: Object })
    public challengeDetails?: CompanyUserChallengeDetailsViewModel;

    @property({ type: Object })
    public company?: CompanyViewModel;

    @state()
    private recollectUserCredentials = false;

    public render() {
        if (!this.challengeDetails || !this.challengeDetails?.isUserLocked) {
            return nothing;
        }

        return html`<div class="my-5">
            <jh-button @click=${this.handleUnlockUser} label="Unlock User" appearance="primary">
                <jha-icon-lock class="fill-current" slot="jh-button-icon"></jha-icon-lock>
            </jh-button>
        </div>`;
    }

    public async handleUnlockUser(e: Event) {
        if (!this.challengeDetails || !this.company || !this.userId) {
            throw new Error('There was a problem unlocking the user.');
        }

        const { abbreviatedAuthenticationMethod } = this.challengeDetails;

        const selectElement = e.target as HTMLSelectElement;
        selectElement.disabled = true;

        const { reason } = await this.dialogService.open(
            html`<div>
                <p class="mb-3">Are you sure you want to unlock this user's authentication?</p>
                <tm-wc-checkbox
                    class="-ml-1 mb-3"
                    name="recollectuser"
                    .initial=${this.recollectUserCredentials}
                    .label=${`Reset user's ${abbreviatedAuthenticationMethod.toLowerCase()} information`}
                    @value-changed=${this.handleRecollectUserCredentialsChange}
                >
                </tm-wc-checkbox>
                <div class="italic">
                    Selecting reset will prompt the user to register again at the next login.
                </div>
            </div>`,
            `Unlock User ${abbreviatedAuthenticationMethod}`,
            {
                buttons: [
                    {
                        label: 'Unlock',
                        reason: DynamicComponentExitReason.Confirm,
                        posture: TmDialogButtonPosture.Primary,
                    },
                    {
                        label: 'Cancel',
                        reason: DynamicComponentExitReason.Cancel,
                        posture: TmDialogButtonPosture.Secondary,
                    },
                ],
            }
        );

        if (reason !== DynamicComponentExitReason.Confirm) {
            selectElement.disabled = false;
            return;
        }

        try {
            const success = await this.companyUserService.unlockCompanyUser(
                this.company.id,
                this.userId,
                this.recollectUserCredentials
            );
            if (!success) {
                throw new Error('There was a problem unlocking the user.');
            }

            this.toastService.show('User successfully unlocked', TmToastPosture.Success);

            this.dispatchEvent(new CustomEvent('user-unlocked', { bubbles: true }));
        } catch {
            this.toastService.show(
                'There was a problem unlocking the user.',
                TmToastPosture.Danger
            );
        } finally {
            selectElement.disabled = false;
        }
    }

    private async handleRecollectUserCredentialsChange(e: CustomEvent<{ value: boolean }>) {
        this.recollectUserCredentials = e.detail.value;
    }
}
